import { AxiosMethods } from '@setvi/shared/enums';

import { pdvCall as call } from 'Services/Query/Pdv/client';
import type {
  PdvAttr,
  PdvAttrAction,
  PdvFlag,
  PdvFlagIssue,
  PdvFlagOutcome,
  PdvFlagReason,
  PdvFlagStatus
} from 'Services/Query/Pdv/types';

/*
 * Every PDV write. Each sets `ownErrorMessage`: the screens word their own failure
 * (pdvErrorMessage), so the global handler only reports to Slack and skips the raw
 * server snackbar.
 */
const meta = { ownErrorMessage: true };

/**
 * Every attribute decision — approve an agent proposal, reject it, remove a derived
 * attribute, restore one, or add one the product team knows belongs. All of it edits
 * SETVI's scoring rubric; none of it touches a CFS system of record.
 *
 * Answers with the attribute as it now stands. Stored scores move on the next nightly
 * run; an override applies at once.
 */
export const decidePdvAttributeMutation = () => ({
  mutationFn: (body: {
    cls: string;
    name: string;
    action: PdvAttrAction;
    note?: string;
  }) => call<PdvAttr>('/pdv/class/attributes', AxiosMethods.POST, { body }),
  meta
});

/**
 * pending -> in_progress when a PM picks it up (assigns it to the caller as well),
 * -> closed when verified. Closing as `not_issue` needs a reason.
 *
 * A 409 means someone else changed the flag since it was loaded.
 */
export const setPdvFlagStatusMutation = () => ({
  mutationFn: ({
    id,
    status,
    close
  }: {
    id: string;
    status: PdvFlagStatus;
    close?: { outcome: PdvFlagOutcome; reason?: PdvFlagReason; note?: string };
  }) =>
    call<PdvFlag>(`/pdv/flags/${id}/status`, AxiosMethods.PUT, {
      body: { status, ...close }
    }),
  meta
});

/** Claiming one tells the rest of the team somebody already has it; null releases it. */
export const assignPdvFlagMutation = () => ({
  mutationFn: ({ id, to }: { id: string; to: string | null }) =>
    call<PdvFlag>(`/pdv/flags/${id}/assign`, AxiosMethods.PUT, {
      body: { to }
    }),
  meta
});

/**
 * The same person reporting the same attribute again while their flag is open adds
 * to that flag, and the API answers with it (same _id).
 */
export const raisePdvFlagMutation = () => ({
  mutationFn: (body: {
    sku: string;
    attr?: string;
    issue: PdvFlagIssue;
    /** required when no attr is named */
    note?: string;
    suggestedValue?: string;
  }) => call<PdvFlag>('/pdv/flags', AxiosMethods.POST, { body }),
  meta
});
